/**
 * ClientWorkspacePage — Top-level orchestrator for the Client Projects Workspace.
 *
 * This is the main page component rendered for users with the `client` role.
 * It fetches project data, manages UI state (tab, sort, search, view mode),
 * and composes child components for filtering, sorting, searching, and display.
 *
 * Key behaviors:
 * - Gated by `client` role via useRolePermissions (renders nothing for other roles)
 * - Fetches projects on mount via clientProjectsService
 * - Aborts in-flight requests on unmount (AbortController)
 * - Derives filtered/sorted/searched projects via useMemo
 * - Renders sectioned layout when "All Projects" tab is active with no search
 * - Renders flat list when a specific tab or search query is active
 * - Shows loading skeleton while fetching
 * - Shows empty state when no projects match filters
 * - On 409 Conflict from actions: shows toast + auto-refreshes project list
 *
 * @see Requirements 1.1, 1.2, 1.4, 2.2, 2.3, 4.2, 5.1, 6.1, 7.1, 8.4, 8.5, 11.1, 11.2
 */

import { useState, useEffect, useMemo, useRef, useCallback, Suspense } from 'react';
import { FolderOpen, SearchX } from 'lucide-react';

import type {
  ClientProjectSummaryDto,
  AttentionItemDto,
  ClientSortOption,
  ClientViewMode,
} from '../../../types/client-workspace';
import { STATUS_TAB_CONFIGS } from '../../../types/client-workspace';

import { clientProjectsService, ConflictError } from '../../../services/clientProjectsService';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { useToastContext } from '../components/primitives/ToastProvider';

import { filterByStatus, computeStatusCounts } from './utils/filtering';
import { sortProjects } from './utils/sorting';
import { searchProjects } from './utils/search';
import { computeAttentionItems } from './utils/attention';
import { assignProjectToSection, type ProjectSection } from './utils/sections';
import { safeStorageGet, safeStorageSet } from './utils/storage';
import { SortControls } from './components/SortControls';
import { ViewModeToggle } from './components/ViewModeToggle';
import { StatusTabNavigation } from './components/StatusTabNavigation';
import {
  FOCUS_VISIBLE_CLASSES,
  announcements,
  manageFocusAfterAction,
  isKeyboardEvent,
} from './utils/accessibility';
import { useAriaAnnouncer } from './hooks/useAriaAnnouncer';
import { DashboardSectionHeader } from '../components/primitives/DashboardSectionHeader';
import { DashboardButton } from '../components/primitives/DashboardButton';
import { PromoBanner } from './components/PromoBanner';
import { authenticatedFetch } from '../../../lib/apiClient';
import { buildApiUrl } from '../../../services/apiUrl';
import {
  submitDashboardProjectCreate,
  submitDashboardProjectDelete,
} from '../projects/api/projectsApi';
import { uploadAdminFile } from '../../../services/viewerProjectService';
import { posthog } from '../../../lib/posthog';
import * as Sentry from '@sentry/react';
import type {
  CreateProjectFileKind,
  CreateProjectFileProgress,
  CreateProjectModalState,
  CreateProjectModalSubmission,
} from '../components/modals/CreateProjectModal';
import type { EntitlementListItemDto } from '../../../server/admin/entitlements/types';
import type { DashboardAsset } from '../types';
import { lazyWithRetry } from '../../../utils/lazyWithRetry';

const CreateProjectModal = lazyWithRetry(() =>
  import('../components/modals/CreateProjectModal').then((m) => ({
    default: m.CreateProjectModal,
  })),
);

const AssetDetailModal = lazyWithRetry(() =>
  import('../components/modals/AssetDetailModal').then((m) => ({
    default: m.AssetDetailModal,
  })),
);

// --- Storage keys ---
const SORT_STORAGE_KEY = 'cw:sort';
const VIEW_MODE_STORAGE_KEY = 'cw:viewMode';

// --- Valid sort options for validation ---
const VALID_SORT_OPTIONS: ClientSortOption[] = [
  'recent_activity',
  'last_updated',
  'due_date',
  'alphabetical',
  'recently_delivered',
  'priority',
  'approval_needed',
];

const VALID_VIEW_MODES: ClientViewMode[] = ['list', 'grid'];

// --- Sort option labels for announcements ---
const SORT_OPTIONS_LABELS: Record<ClientSortOption, string> = {
  recent_activity: 'Most Recent Activity',
  last_updated: 'Last Updated',
  due_date: 'Due Date',
  alphabetical: 'Alphabetical',
  recently_delivered: 'Recently Delivered',
  priority: 'Priority',
  approval_needed: 'Approval Needed',
};

// --- Component Props ---
export interface ClientWorkspacePageProps {
  role: 'client';
  displayName: string;
  onSignOut: () => Promise<void>;
  signOutError: string | null;
}

// --- Loading state type ---
type LoadingState = 'idle' | 'loading' | 'success' | 'error';

export function ClientWorkspacePage({
  role,
  displayName,
  signOutError,
}: ClientWorkspacePageProps) {
  // --- Role gating via useRolePermissions ---
  // The component only renders for the `client` role. useRolePermissions provides
  // the permission set for the given role. If the role is not 'client', we bail
  // after all hooks (see gate below). The permissions object is available for
  // future use by child components (e.g., conditional action buttons).
  const permissions = useRolePermissions(role);

  // --- Toast for conflict notifications ---
  const toast = useToastContext();

  // --- Data state ---
  const [projects, setProjects] = useState<ClientProjectSummaryDto[]>([]);
  const [attentionItems, setAttentionItems] = useState<AttentionItemDto[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // --- UI state ---
  const [activeTabId, setActiveTabId] = useState<string>('all');
  const [sortOption, setSortOption] = useState<ClientSortOption>(() => {
    const stored = safeStorageGet(
      typeof window !== 'undefined' ? window.sessionStorage : null,
      SORT_STORAGE_KEY,
    );
    if (stored && VALID_SORT_OPTIONS.includes(stored as ClientSortOption)) {
      return stored as ClientSortOption;
    }
    return 'recent_activity';
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<ClientViewMode>(() => {
    const stored = safeStorageGet(
      typeof window !== 'undefined' ? window.localStorage : null,
      VIEW_MODE_STORAGE_KEY,
    );
    if (stored && VALID_VIEW_MODES.includes(stored as ClientViewMode)) {
      return stored as ClientViewMode;
    }
    return 'list';
  });

  // --- Refs ---
  const abortControllerRef = useRef<AbortController | null>(null);
  const resultsRegionRef = useRef<HTMLDivElement | null>(null);
  const lastInteractionWasKeyboard = useRef(false);

  // --- Debounced aria-live announcer (prevents spam on rapid state changes) ---
  const { regionRef: announcerRef, announce } = useAriaAnnouncer({ debounceMs: 300 });

  // --- Entitlement state (gates project creation for client role) ---
  const [entitlements, setEntitlements] = useState<EntitlementListItemDto[]>([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const [isCreateProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [createProjectModalState, setCreateProjectModalState] =
    useState<CreateProjectModalState>({
      phase: 'idle',
      errorMessage: null,
      fileProgress: [],
    });
  const closeCreateProjectModal = useCallback(() => {
    setCreateProjectModalOpen(false);
    setCreateProjectModalState({
      phase: 'idle',
      errorMessage: null,
      fileProgress: [],
    });
  }, []);

  // --- 3D viewer modal state ---
  const [viewerProject, setViewerProject] = useState<ClientProjectSummaryDto | null>(null);

  const handleOpenViewer = useCallback((project: ClientProjectSummaryDto) => {
    setViewerProject(project);
  }, []);

  const handleCloseViewer = useCallback(() => {
    setViewerProject(null);
  }, []);

  // Fetch entitlements on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchEntitlements() {
      try {
        const res = await authenticatedFetch(buildApiUrl('/api/admin/entitlements'));
        const json = await res.json();
        if (!cancelled && json.success) {
          setEntitlements(json.data.items ?? []);
        }
      } catch {
        // Non-blocking — button stays disabled if fetch fails
      } finally {
        if (!cancelled) setEntitlementsLoading(false);
      }
    }
    void fetchEntitlements();
    return () => { cancelled = true; };
  }, []);

  // Derived: available entitlements (active + no project yet)
  const availableEntitlements = useMemo(
    () => entitlements.filter((e) => !e.hasProject && e.status === 'active'),
    [entitlements],
  );

  const canCreateProject = permissions.canCreateProject && availableEntitlements.length > 0 && !entitlementsLoading;

  // --- Data fetching ---
  const fetchProjects = useCallback(async () => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoadingState('loading');
    setErrorMessage(null);

    try {
      const response = await clientProjectsService.listProjects(controller.signal);
      // Don't update state if this request was aborted
      if (controller.signal.aborted) return;

      setProjects(response.data.projects);
      setAttentionItems(computeAttentionItems(response.data.projects));
      setLoadingState('success');
    } catch (err: unknown) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') return;
      if (controller.signal.aborted) return;

      setLoadingState('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unable to load projects. Please try again.',
      );
    }
  }, []);

  // Fetch on mount, abort on unmount
  useEffect(() => {
    fetchProjects();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProjects]);

  // --- Persist sort option to sessionStorage ---
  const handleSortChange = useCallback((option: ClientSortOption) => {
    setSortOption(option);
    safeStorageSet(
      typeof window !== 'undefined' ? window.sessionStorage : null,
      SORT_STORAGE_KEY,
      option,
    );
    // Announce sort change for screen readers
    const label = SORT_OPTIONS_LABELS[option] ?? option;
    announce(announcements.sortChanged(label));
  }, [announce]);

  // --- Persist view mode to localStorage ---
  const handleViewModeChange = useCallback((mode: ClientViewMode) => {
    setViewMode(mode);
    safeStorageSet(
      typeof window !== 'undefined' ? window.localStorage : null,
      VIEW_MODE_STORAGE_KEY,
      mode,
    );
  }, []);

  // --- Derived data ---

  // Get the active tab's status filter
  const activeStatusFilter = useMemo(() => {
    const tab = STATUS_TAB_CONFIGS.find((t) => t.id === activeTabId);
    return tab?.statusFilter ?? null;
  }, [activeTabId]);

  // Status counts — always computed from the FULL project list
  const statusCounts = useMemo(
    () => computeStatusCounts(projects),
    [projects],
  );

  // Attention project IDs set (for section assignment)
  const attentionProjectIds = useMemo(
    () => new Set(attentionItems.map((item) => item.sourceProjectId)),
    [attentionItems],
  );

  // Step 1: Filter by status tab
  const filteredByStatus = useMemo(
    () => filterByStatus(projects, activeStatusFilter),
    [projects, activeStatusFilter],
  );

  // Step 2: Apply search (if query is present)
  const searchedProjects = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return filteredByStatus;
    return searchProjects(filteredByStatus, trimmed);
  }, [filteredByStatus, searchQuery]);

  // Step 3: Sort the results
  const sortedProjects = useMemo(
    () => sortProjects(searchedProjects, sortOption),
    [searchedProjects, sortOption],
  );

  // Determine if we should show sectioned layout:
  // Sectioned when "All Projects" tab is active AND no search query
  const showSectionedLayout = activeTabId === 'all' && !searchQuery.trim();

  // --- Announce filter/search results for screen readers (debounced) ---
  useEffect(() => {
    if (loadingState !== 'success') return;

    const trimmedSearch = searchQuery.trim();
    if (trimmedSearch) {
      announce(announcements.searchResults(sortedProjects.length, trimmedSearch));
    } else {
      const tabConfig = STATUS_TAB_CONFIGS.find((t) => t.id === activeTabId);
      const tabLabel = tabConfig?.label ?? 'All Projects';
      announce(announcements.filterResults(sortedProjects.length, tabLabel));
    }

    // Move focus to results region if the last interaction was keyboard-initiated
    manageFocusAfterAction(resultsRegionRef, lastInteractionWasKeyboard.current);
    lastInteractionWasKeyboard.current = false;
  }, [activeTabId, sortedProjects.length, searchQuery, loadingState, announce]);

  // Assign projects to sections (only when sectioned layout is active)
  const sectionedProjects = useMemo(() => {
    if (!showSectionedLayout) return null;

    const sections: Record<NonNullable<ProjectSection>, ClientProjectSummaryDto[]> = {
      attention: [],
      active: [],
      recently_delivered: [],
    };

    for (const project of sortedProjects) {
      const section = assignProjectToSection(project, attentionProjectIds);
      if (section) {
        sections[section].push(project);
      }
    }

    return sections;
  }, [showSectionedLayout, sortedProjects, attentionProjectIds]);

  // --- Create project flow: drives the modal state machine for the client.
  // - Validates file sizes client-side
  // - Calls backend create with entitlement
  // - Streams per-file uploads with progress
  // - On any failure, rolls back the project (DELETE) and shows failure UI
  // - On success, refreshes the project list
  const handleCreateProject = useCallback(
    async (submission: CreateProjectModalSubmission) => {
      const selectedFiles: Array<{ kind: CreateProjectFileKind; file: File }> = [
        submission.cadFile ? { kind: 'cad' as const, file: submission.cadFile } : null,
        submission.modelFile ? { kind: 'model' as const, file: submission.modelFile } : null,
        submission.pdfFile ? { kind: 'pdf' as const, file: submission.pdfFile } : null,
        submission.videoFile ? { kind: 'video' as const, file: submission.videoFile } : null,
      ].filter((value): value is { kind: CreateProjectFileKind; file: File } => Boolean(value));

      const MAX_FILE_SIZES: Record<CreateProjectFileKind, number> = {
        model: 200 * 1024 * 1024,
        cad: 500 * 1024 * 1024,
        pdf: 50 * 1024 * 1024,
        video: 500 * 1024 * 1024,
      };

      setCreateProjectModalState({
        phase: 'validating',
        errorMessage: null,
        fileProgress: selectedFiles.map<CreateProjectFileProgress>((f) => ({
          kind: f.kind,
          fileName: f.file.name,
          status: 'pending',
          message: null,
        })),
      });

      for (const file of selectedFiles) {
        const maxSize = MAX_FILE_SIZES[file.kind] ?? 50 * 1024 * 1024;
        if (file.file.size > maxSize) {
          const message = `File "${file.file.name}" exceeds the ${Math.round(maxSize / (1024 * 1024))}MB limit for ${file.kind} files.`;
          setCreateProjectModalState((prev) => ({
            phase: 'failed',
            errorMessage: message,
            fileProgress: prev.fileProgress.map((entry) =>
              entry.kind === file.kind && entry.fileName === file.file.name
                ? { ...entry, status: 'failed', message }
                : entry,
            ),
          }));
          Sentry.addBreadcrumb({
            category: 'project.create',
            level: 'warning',
            message: 'Pre-flight file size check failed',
            data: {
              actorRole: 'client',
              kind: file.kind,
              fileName: file.file.name,
              sizeBytes: file.file.size,
              maxBytes: maxSize,
            },
          });
          return;
        }
      }

      posthog.capture('project_creation_started', {
        title: submission.title,
        file_count: selectedFiles.length,
        actor_role: 'client',
      });

      setCreateProjectModalState((prev) => ({
        ...prev,
        phase: 'creating',
        errorMessage: null,
      }));

      const createResult = await submitDashboardProjectCreate({
        title: submission.title,
        companyName: '',
        entitlementId: submission.entitlementId ?? null,
      });

      if (!createResult.ok) {
        setCreateProjectModalState((prev) => ({
          ...prev,
          phase: 'failed',
          errorMessage: createResult.error.message,
        }));
        posthog.capture('project_creation_failed', {
          title: submission.title,
          stage: 'creating',
          actor_role: 'client',
          message: createResult.error.message,
        });
        Sentry.captureMessage('Client project create rejected', {
          level: 'error',
          tags: {
            feature: 'project-create',
            stage: 'creating',
            actor_role: 'client',
          },
          contexts: {
            create: {
              title: submission.title,
              entitlementId: submission.entitlementId ?? null,
              errorCode: createResult.error.code ?? null,
              errorMessage: createResult.error.message,
            },
          },
        });
        return;
      }

      const newProjectId = createResult.data.item.id;

      if (selectedFiles.length === 0) {
        setCreateProjectModalState((prev) => ({
          ...prev,
          phase: 'success',
          errorMessage: null,
        }));
        posthog.capture('project_creation_succeeded', {
          project_id: newProjectId,
          title: submission.title,
          file_count: 0,
          actor_role: 'client',
        });
        void fetchProjects();
        return;
      }

      setCreateProjectModalState((prev) => ({
        ...prev,
        phase: 'uploading',
        errorMessage: null,
      }));
      posthog.capture('project_creation_uploading', {
        project_id: newProjectId,
        file_count: selectedFiles.length,
        actor_role: 'client',
      });

      const failed: Array<{ kind: CreateProjectFileKind; fileName: string; message: string }> = [];

      for (const file of selectedFiles) {
        setCreateProjectModalState((prev) => ({
          ...prev,
          fileProgress: prev.fileProgress.map((entry) =>
            entry.kind === file.kind && entry.fileName === file.file.name
              ? { ...entry, status: 'uploading', message: null }
              : entry,
          ),
        }));
        try {
          await uploadAdminFile({ file: file.file, projectId: newProjectId });
          setCreateProjectModalState((prev) => ({
            ...prev,
            fileProgress: prev.fileProgress.map((entry) =>
              entry.kind === file.kind && entry.fileName === file.file.name
                ? { ...entry, status: 'success', message: null }
                : entry,
            ),
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Upload failed.';
          failed.push({ kind: file.kind, fileName: file.file.name, message });
          setCreateProjectModalState((prev) => ({
            ...prev,
            fileProgress: prev.fileProgress.map((entry) =>
              entry.kind === file.kind && entry.fileName === file.file.name
                ? { ...entry, status: 'failed', message }
                : entry,
            ),
          }));
          Sentry.addBreadcrumb({
            category: 'project.upload',
            level: 'warning',
            message: 'Project attachment upload failed (client)',
            data: {
              actorRole: 'client',
              projectId: newProjectId,
              kind: file.kind,
              fileName: file.file.name,
              sizeBytes: file.file.size,
              mimeType: file.file.type,
              errorMessage: message,
            },
          });
          Sentry.captureException(error, {
            tags: {
              feature: 'project-create',
              stage: 'upload',
              actor_role: 'client',
              kind: file.kind,
            },
            contexts: {
              upload: {
                projectId: newProjectId,
                fileName: file.file.name,
                sizeBytes: file.file.size,
                mimeType: file.file.type,
              },
            },
          });
        }
      }

      if (failed.length > 0) {
        setCreateProjectModalState((prev) => ({
          ...prev,
          phase: 'failed_rolling_back',
          errorMessage: `${failed.length} file${failed.length === 1 ? '' : 's'} failed to upload. Rolling back…`,
        }));
        posthog.capture('project_creation_failed', {
          project_id: newProjectId,
          title: submission.title,
          stage: 'uploading',
          failed_count: failed.length,
          actor_role: 'client',
        });
        Sentry.addBreadcrumb({
          category: 'project.create',
          level: 'error',
          message: 'Project file uploads failed; rolling back (client)',
          data: {
            actorRole: 'client',
            projectId: newProjectId,
            selectedFileCount: selectedFiles.length,
            failedCount: failed.length,
            failures: failed.map((f) => ({
              fileName: f.fileName,
              kind: f.kind,
              message: f.message,
            })),
          },
        });
        const rollback = await submitDashboardProjectDelete(newProjectId);
        const firstFail = failed[0];
        const failureMessage = firstFail
          ? `Upload failed: ${firstFail.fileName} — ${firstFail.message}`
          : 'One or more files failed to upload.';
        setCreateProjectModalState((prev) => ({
          ...prev,
          phase: 'failed',
          errorMessage: rollback.ok
            ? `${failureMessage} The project was not created.`
            : `${failureMessage} The project could not be fully rolled back. Please refresh.`,
        }));
        posthog.capture('project_creation_rolled_back', {
          project_id: newProjectId,
          title: submission.title,
          rollback_ok: rollback.ok,
          failed_count: failed.length,
          actor_role: 'client',
        });
        Sentry.captureMessage(
          rollback.ok
            ? 'Client project rolled back after upload failure'
            : 'Client project rollback failed after upload failure',
          {
            level: rollback.ok ? 'warning' : 'error',
            tags: {
              feature: 'project-create',
              stage: 'rollback',
              actor_role: 'client',
              rollback_ok: String(rollback.ok),
            },
            contexts: {
              create: {
                projectId: newProjectId,
                title: submission.title,
                failedCount: failed.length,
                firstFailure: firstFail ?? null,
              },
            },
          },
        );
        return;
      }

      setCreateProjectModalState((prev) => ({
        ...prev,
        phase: 'success',
        errorMessage: null,
      }));
      posthog.capture('project_creation_succeeded', {
        project_id: newProjectId,
        title: submission.title,
        file_count: selectedFiles.length,
        actor_role: 'client',
      });
      void fetchProjects();
    },
    [fetchProjects],
  );

  // --- 409 Conflict handler ---
  // When an action (approve, revision) returns 409 Conflict, show a toast and auto-refresh.
  const handleConflict = useCallback((error: unknown) => {
    if (error instanceof ConflictError) {
      toast.error(error.message || 'Project status has changed. Refreshing...');
      // Auto-refresh the project list to get the latest state
      fetchProjects();
      return true;
    }
    return false;
  }, [toast, fetchProjects]);

  // --- Role gate ---
  // The component only renders for the `client` role. If the role is not `client`,
  // return null without rendering any UI. This is an additional safety layer
  // (the parent already gates by role, but this ensures isolation per Req 11.1, 11.2).
  // `permissions` from useRolePermissions will be passed to child components in later tasks.
  if (role !== 'client') {
    return null;
  }

  // --- Render ---

  // Loading state
  if (loadingState === 'idle' || loadingState === 'loading') {
    return (
      <div className="flex flex-col gap-6" data-testid="client-workspace-loading">
        {/* Header skeleton */}
        <div className="flex flex-col gap-4">
          <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-800/50" />
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-9 w-24 animate-pulse rounded-full bg-slate-800/40"
              />
            ))}
          </div>
        </div>
        {/* Project cards skeleton */}
        <div className="flex flex-col gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl border border-white/5 bg-slate-900/50"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (loadingState === 'error') {
    return (
      <div
        className="flex min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-white/10 bg-slate-900/50 p-8 text-center"
        data-testid="client-workspace-error"
      >
        <div className="text-sm text-slate-400">
          {errorMessage || 'Unable to load projects.'}
        </div>
        <button
          className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-300 transition-colors hover:bg-sky-500/20"
          onClick={fetchProjects}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state — no projects at all
  if (projects.length === 0) {
    return (
      <div
        className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 p-8 text-center"
        data-testid="client-workspace-empty"
      >
        <FolderOpen className="text-slate-500" />
        <h2 className="text-base font-semibold text-slate-200">
          No projects available
        </h2>
        <p className="max-w-sm text-sm text-slate-400">
          Projects are assigned by your admin. Once you're added to a project, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="client-workspace">
      {/* Sign-out error banner */}
      {signOutError && (
        <div className="rounded-xl border border-rose-400/20 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">
          {signOutError}
        </div>
      )}

      {/* Page header with greeting */}
      <DashboardSectionHeader
        eyebrow="Workspace"
        title="Projects"
        meta={displayName ? `Welcome back, ${displayName}` : null}
      >
        <DashboardButton
          className="w-full whitespace-nowrap sm:w-auto sm:shrink-0"
          onClick={() => setCreateProjectModalOpen(true)}
          disabled={!canCreateProject}
          type="button"
        >
          <span
            aria-hidden="true"
            className="material-symbols-outlined text-[14px]"
          >
            add
          </span>
          New Project
        </DashboardButton>
      </DashboardSectionHeader>

      {/* Promo banner — self-contained, handles own data fetching and visibility */}
      <PromoBanner />

      {/* Controls row: Search, Sort, View Mode
          - Stacks vertically on mobile (<768px)
          - Horizontal row on tablet+ (md:)
          Requirements: 9.1, 9.2, 9.6, 9.7 */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {/* Search input — matches dashboard section search pattern */}
        <div className="relative w-full max-w-2xl">
          <span
            aria-hidden="true"
            className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-slate-500"
          >
            search
          </span>
          <input
            aria-label="Search projects"
            className="dashboard-content-search typography-input h-9 w-full rounded-lg border border-white/10 bg-slate-900/60 pl-9 pr-3 text-xs text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-sky-500/60 focus:ring-1 focus:ring-sky-500/40"
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search projects..."
            type="text"
            value={searchQuery}
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Sort controls — renders as bottom sheet on mobile (<768px), dropdown on desktop */}
          <SortControls value={sortOption} onChange={handleSortChange} />

          {/* View mode toggle — hidden on mobile (<768px), defaults to List View */}
          <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
        </div>
      </div>

      {/* Status tab navigation — horizontally scrollable on mobile
          Requirements: 9.2 */}
      <StatusTabNavigation
        activeTab={activeTabId}
        statusCounts={statusCounts}
        onTabChange={(tabId) => {
          lastInteractionWasKeyboard.current = false;
          setActiveTabId(tabId);
        }}
        filteredCount={sortedProjects.length}
      />

      {/* Needs Attention section (only in sectioned layout) */}
      {showSectionedLayout && attentionItems.length > 0 && (
        <section aria-label="Needs your attention" data-testid="needs-attention-section">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-amber-400">
            Needs Your Attention
            <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold tabular-nums text-amber-300">
              {attentionItems.length > 99 ? '99+' : attentionItems.length}
            </span>
          </h2>
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
                : 'flex flex-col gap-2'
            }
          >
            {attentionItems.map((item) => (
              <div
                className={
                  viewMode === 'grid'
                    ? 'flex flex-col gap-2 rounded-lg border border-amber-500/15 bg-amber-950/20 p-4 text-sm'
                    : 'flex items-center gap-3 rounded-lg border border-amber-500/15 bg-amber-950/20 px-4 py-3 text-sm'
                }
                key={item.id}
              >
                <span className="text-xs font-medium capitalize text-amber-300">
                  {item.type.replace(/_/g, ' ')}
                </span>
                <span className="text-slate-300">{item.sourceName}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No attention items message (sectioned layout only) */}
      {showSectionedLayout && attentionItems.length === 0 && (
        <div className="rounded-lg border border-emerald-500/15 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-300">
          All caught up — no items need your attention.
        </div>
      )}

      {/* Project list content */}
      {sortedProjects.length === 0 ? (
        // Empty state for filtered/searched results
        <div
          className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 p-6 text-center"
          data-testid="client-workspace-no-results"
        >
          <SearchX className="text-slate-500" />
          <p className="text-sm text-slate-400">
            {searchQuery.trim()
              ? `No projects match "${searchQuery.trim()}". Try removing filters or checking your spelling.`
              : `No projects with "${STATUS_TAB_CONFIGS.find((t) => t.id === activeTabId)?.label ?? activeTabId}" status.`}
          </p>
        </div>
      ) : showSectionedLayout && sectionedProjects ? (
        // Sectioned layout
        <div className="flex flex-col gap-8">
          {sectionedProjects.active.length > 0 && (
            <ProjectSectionBlock
              label="Active Projects"
              projects={sectionedProjects.active}
              viewMode={viewMode}
              onOpenViewer={handleOpenViewer}
            />
          )}
          {sectionedProjects.recently_delivered.length > 0 && (
            <ProjectSectionBlock
              label="Recently Delivered"
              projects={sectionedProjects.recently_delivered}
              viewMode={viewMode}
              onOpenViewer={handleOpenViewer}
            />
          )}
        </div>
      ) : (
        // Flat list/grid layout
        <ProjectListLayout
          projects={sortedProjects}
          viewMode={viewMode}
          onOpenViewer={handleOpenViewer}
        />
      )}

      {/* Debounced aria-live region for dynamic content announcements */}
      <div
        ref={announcerRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="workspace-aria-announcer"
      />

      {/* Focusable results region heading for keyboard-initiated focus management */}
      <div
        ref={resultsRegionRef}
        tabIndex={-1}
        className="sr-only"
        aria-label="Project results"
      />

      {/* Create Project Modal (entitlement-gated) */}
      {isCreateProjectModalOpen && (
        <Suspense fallback={null}>
          <CreateProjectModal
            onClose={closeCreateProjectModal}
            entitlements={entitlements}
            entitlementsLoading={entitlementsLoading}
            requireEntitlement={true}
            state={createProjectModalState}
            onCreate={handleCreateProject}
          />
        </Suspense>
      )}

      {/* 3D Model Viewer Modal — opened from per-project more_horiz button */}
      {viewerProject && (
        <Suspense fallback={null}>
          <AssetDetailModal
            asset={projectToDashboardAsset(viewerProject)}
            onClose={handleCloseViewer}
          />
        </Suspense>
      )}
    </div>
  );
}

// --- Internal sub-components ---

interface ProjectSectionBlockProps {
  label: string;
  projects: ClientProjectSummaryDto[];
  viewMode: ClientViewMode;
  onOpenViewer?: (project: ClientProjectSummaryDto) => void;
}

function ProjectSectionBlock({ label, projects, viewMode, onOpenViewer }: ProjectSectionBlockProps) {
  return (
    <section aria-label={label}>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
        {label}
        <span className="ml-2 text-[10px] font-normal tabular-nums text-slate-500">
          ({projects.length})
        </span>
      </h2>
      <ProjectListLayout projects={projects} viewMode={viewMode} onOpenViewer={onOpenViewer} />
    </section>
  );
}

interface ProjectListLayoutProps {
  projects: ClientProjectSummaryDto[];
  viewMode: ClientViewMode;
  onOpenViewer?: (project: ClientProjectSummaryDto) => void;
}

function ProjectListLayout({ projects, viewMode, onOpenViewer }: ProjectListLayoutProps) {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <ProjectCardPlaceholder
            key={project.id}
            project={project}
            onOpenViewer={onOpenViewer}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {projects.map((project) => (
        <ProjectRowPlaceholder
          key={project.id}
          project={project}
          onOpenViewer={onOpenViewer}
        />
      ))}
    </div>
  );
}

/**
 * Placeholder project card — will be replaced by the full ProjectCard component
 * in a later task. Provides basic display for integration testing.
 */
function ProjectCardPlaceholder({
  project,
  onOpenViewer,
}: {
  project: ClientProjectSummaryDto;
  onOpenViewer?: (project: ClientProjectSummaryDto) => void;
}) {
  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-white/10 bg-slate-900/60 p-4 transition-colors hover:border-white/20"
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-slate-100">
          {project.name}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {project.status.replace(/_/g, ' ')}
          </span>
          {onOpenViewer && (
            <button
              type="button"
              aria-label={`Open 3D model viewer for ${project.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenViewer(project);
              }}
              className="inline-flex size-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-sm">
                more_horiz
              </span>
            </button>
          )}
        </div>
      </div>
      {project.thumbnailUrl && (
        <div className="h-24 overflow-hidden rounded-lg bg-slate-800/50">
          <img
            alt={`${project.name} thumbnail`}
            className="size-full object-cover"
            src={project.thumbnailUrl}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Placeholder project row — will be replaced by the full ProjectRow component
 * in a later task. Provides basic display for integration testing.
 */
function ProjectRowPlaceholder({
  project,
  onOpenViewer,
}: {
  project: ClientProjectSummaryDto;
  onOpenViewer?: (project: ClientProjectSummaryDto) => void;
}) {
  return (
    <div
      className="flex items-center gap-4 rounded-lg border border-white/10 bg-slate-900/60 px-4 py-3 transition-colors hover:border-white/20"
      data-testid={`project-row-${project.id}`}
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-100">
        {project.name}
      </span>
      <span className="shrink-0 rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {project.status.replace(/_/g, ' ')}
      </span>
      {onOpenViewer && (
        <button
          type="button"
          aria-label={`Open 3D model viewer for ${project.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenViewer(project);
          }}
          className="shrink-0 inline-flex size-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-sm">
            more_horiz
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Maps a ClientProjectSummaryDto to a minimal DashboardAsset shape so we can
 * reuse the existing AssetDetailModal without backend changes. The modal's
 * model branch already renders a graceful 3D placeholder when no runtime
 * preview record is found, so this synthetic asset works out of the box.
 */
function projectToDashboardAsset(project: ClientProjectSummaryDto): DashboardAsset {
  return {
    id: project.id,
    name: project.name,
    type: 'model',
    readiness: 'ready',
    sizeLabel: '—',
    updatedAtLabel: project.lastActivityAt,
    description: '',
    metadata: [],
    tags: [],
  };
}
